import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Trash2, Upload } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const inputRef = useRef(null);

  const load = () =>
    api.get("/business/library").then(({ data }) => setDocs(data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/business/library", fd);
      toast.success("Document uploaded");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/business/library/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      setConfirmDelete(null);
      toast.success("Document deleted");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">Document library</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Upload PDFs and files here, then attach them to checkbox options in the form builder. Customers receive them in the auto-responder email.
          </p>
        </div>
        <div>
          <input ref={inputRef} type="file" className="hidden" onChange={upload} data-testid="document-upload-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-full" data-testid="document-upload-button">
            <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload document"}
          </Button>
        </div>
      </div>

      <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E5] text-left text-xs uppercase tracking-widest text-neutral-400">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Size</th>
              <th className="px-5 py-3">Uploaded</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]">
            {!docs ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={4}>Loading…</td></tr>
            ) : docs.length === 0 ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={4} data-testid="documents-empty">No documents yet. Upload your first PDF.</td></tr>
            ) : (
              docs.map((d) => (
                <tr key={d.id} data-testid={`document-row-${d.id}`}>
                  <td className="px-5 py-3.5">
                    <a href={`${BACKEND}/api/uploads/${d.filename}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-medium hover:underline" data-testid={`document-link-${d.id}`}>
                      <FileText className="h-4 w-4 text-neutral-400 shrink-0" />
                      <span className="truncate max-w-xs">{d.name}</span>
                    </a>
                  </td>
                  <td className="px-5 py-3.5 text-neutral-500">{(d.size / 1024).toFixed(0)} KB</td>
                  <td className="px-5 py-3.5 text-neutral-500">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3.5 text-right">
                    {confirmDelete === d.id ? (
                      <span className="inline-flex items-center gap-2" data-testid={`document-delete-confirm-${d.id}`}>
                        <span className="text-xs text-neutral-500">Delete this document?</span>
                        <Button size="sm" variant="outline" className="rounded-full h-7 text-xs border-red-300 text-red-600" onClick={() => remove(d.id)} data-testid={`document-delete-yes-${d.id}`}>Delete</Button>
                        <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs" onClick={() => setConfirmDelete(null)} data-testid={`document-delete-no-${d.id}`}>Cancel</Button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDelete(d.id)} className="text-neutral-400 hover:text-red-600 transition-colors duration-150" data-testid={`document-delete-${d.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
